import { Divider, Grid, Typography } from "@mui/material";
import { useMemo } from "react";
import {
  BoolParamInput,
  EnumParamInput,
  EnumsParamInput,
  EnumListInput,
  IntParamInput,
  MapParamInput,
  MetricsParamInput,
  StringParamInput,
  StringsParamInput,
  TimezoneParamInput,
  YamlParamInput,
  FileLogSortInput,
  OTTLFieldInput,
} from ".";
import { ParameterDefinition, ParameterType } from "../../../graphql/generated";
import { useResourceFormValues } from "../ResourceFormContext";
import { AWSCloudwatchInput } from "./AWSCloudwatchFieldInput";
import { FractionParamInput } from "./FractionParamInput";

export interface ParamInputProps<T> {
  definition: ParameterDefinition;
  value?: T;
  onValueChange?: (v: T) => void;
  readOnly?: boolean;
}

export const ParameterInput: React.FC<{
  definition: ParameterDefinition;
  readOnly?: boolean;
}> = ({ definition, readOnly }) => {
  const { formValues, setFormValues } = useResourceFormValues();
  const onValueChange = useMemo(
    () => (newValue: any) => {
      setFormValues((prev) => ({ ...prev, [definition.name]: newValue }));
    },
    [definition.name, setFormValues],
  );

  const subHeader: JSX.Element | null = useMemo(() => {
    if (definition.options.subHeader) {
      return (
        <Grid item xs={12}>
          <Typography>{definition.options.subHeader}</Typography>
        </Grid>
      );
    }

    return null;
  }, [definition.options.subHeader]);

  const divider: JSX.Element | null = useMemo(() => {
    if (definition.options.horizontalDivider) {
      return (
        <Grid item xs={12}>
          <Divider />
        </Grid>
      );
    }
    return null;
  }, [definition.options.horizontalDivider]);

  const Control: JSX.Element = useMemo(() => {
    const dataTestId = `parameter-input-${definition.name.toLowerCase()}`;
    switch (definition.type) {
      case ParameterType.String:
        return (
          <StringParamInput
            definition={definition}
            value={formValues[definition.name]}
            onValueChange={onValueChange}
            readOnly={readOnly}
            data-testid={dataTestId}
          />
        );

      case ParameterType.Strings:
        return (
          <StringsParamInput
            definition={definition}
            value={formValues[definition.name]}
            onValueChange={onValueChange}
            readOnly={readOnly}
            data-testid={dataTestId}
          />
        );

      case ParameterType.Enum:
        return (
          <EnumParamInput
            definition={definition}
            value={formValues[definition.name]}
            onValueChange={onValueChange}
            readOnly={readOnly}
            data-testid={dataTestId}
          />
        );

      case ParameterType.Enums:
        return (
          <EnumsParamInput
            definition={definition}
            value={formValues[definition.name]}
            onValueChange={onValueChange}
            readOnly={readOnly}
            data-testid={dataTestId}
          />
        );

      case ParameterType.EnumList:
        return (
          <EnumListInput
            definition={definition}
            value={formValues[definition.name]}
            onValueChange={onValueChange}
            readOnly={readOnly}
            data-testid={dataTestId}
          />
        );

      case ParameterType.Bool:
        return (
          <BoolParamInput
            definition={definition}
            value={formValues[definition.name]}
            onValueChange={onValueChange}
            readOnly={readOnly}
            data-testid={definition.name}
          />
        );

      case ParameterType.Int:
        return (
          <IntParamInput
            definition={definition}
            value={formValues[definition.name]}
            onValueChange={onValueChange}
            readOnly={readOnly}
            data-testid={dataTestId}
          />
        );

      case ParameterType.Fraction:
        return (
          <FractionParamInput
            definition={definition}
            value={formValues[definition.name]}
            onValueChange={onValueChange}
            readOnly={readOnly}
            data-testid={dataTestId}
          />
        );

      case ParameterType.Map:
        return (
          <MapParamInput
            definition={definition}
            value={formValues[definition.name]}
            onValueChange={onValueChange}
            readOnly={readOnly}
            data-testid={dataTestId}
          />
        );

      case ParameterType.Yaml:
        return (
          <YamlParamInput
            definition={definition}
            value={formValues[definition.name]}
            onValueChange={onValueChange}
            readOnly={readOnly}
            data-testid={dataTestId}
          />
        );

      case ParameterType.Timezone:
        return (
          <TimezoneParamInput
            definition={definition}
            value={formValues[definition.name]}
            onValueChange={onValueChange}
            readOnly={readOnly}
            data-testid={dataTestId}
          />
        );

      case ParameterType.Metrics:
        return (
          <MetricsParamInput
            definition={definition}
            value={formValues[definition.name]}
            onValueChange={onValueChange}
            readOnly={readOnly}
            data-testid={dataTestId}
          />
        );

      case ParameterType.AwsCloudwatchNamedField:
        return (
          <AWSCloudwatchInput
            definition={definition}
            value={formValues[definition.name]}
            onValueChange={onValueChange}
            readOnly={readOnly}
            data-testid={dataTestId}
          />
        );
      case ParameterType.FileLogSort:
        return (
          <FileLogSortInput
            definition={definition}
            value={formValues[definition.name]}
            onValueChange={onValueChange}
            readOnly={readOnly}
            data-testid={dataTestId}
          />
        );
      case ParameterType.MapToEnum:
        return (
          <MapParamInput
            definition={definition}
            value={formValues[definition.name]}
            onValueChange={onValueChange}
            readOnly={readOnly}
            data-testid={dataTestId}
          />
        );
      case ParameterType.OttlField:
        return (
          <OTTLFieldInput
            definition={definition}
            value={formValues[definition.name]}
            onValueChange={onValueChange}
            readOnly={readOnly}
            data-testid={dataTestId}
          />
        );
    }
  }, [definition, formValues, onValueChange, readOnly]);

  const gridColumns = useMemo(() => {
    if (isMetricsType(definition)) {
      return 12;
    }

    if (isTelemetryHeader(definition)) {
      return 12;
    }

    if (isSectionHeader(definition)) {
      return 12;
    }

    if (isAWSCloudwatch(definition)) {
      return 12;
    }

    if (isFileLogSort(definition)) {
      return 12;
    }

    return definition.options.gridColumns ?? 6;
  }, [definition]);

  return (
    <>
      {subHeader}
      <Grid item xs={gridColumns}>
        {Control}
      </Grid>
      {divider}
    </>
  );
};

function isTelemetryHeader(definition: ParameterDefinition) {
  return ["enable_metrics", "enable_logs", "enable_traces"].includes(
    definition.name,
  );
}

function isSectionHeader(definition: ParameterDefinition) {
  return definition.options.sectionHeader === true;
}

function isMetricsType(definition: ParameterDefinition) {
  return definition.type === "metrics";
}

function isAWSCloudwatch(definition: ParameterDefinition) {
  return definition.type === "awsCloudwatchNamedField";
}

function isFileLogSort(definition: ParameterDefinition) {
  return definition.type === "fileLogSort";
}
